import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import { Divider } from '@material-ui/core';
import { IoIosArrowBack } from 'react-icons/io';

import { useToast } from '../../hooks/toast';
import { useAuth } from '../../hooks/auth';

import api from '../../services/api';

import getValidationErrors from '../../utils/getValidationErrors';

import Input from '../../newComponents/UnformInput';
import Select from '../../newComponents/UnformSelect';
import Button from '../../newComponents/Button';

import { useRegimeTributario } from '../../hooks/useRegimeTributario';
import {
  getCstCsosnOptions,
  CST_PIS_COFINS_OPTIONS,
} from '../../constants/fiscal';

import * as S from './styles';

interface GrupoFormData {
  descricao: string;
  cst_csosn?: string;
  aliquota_icms?: string;
  reducao_base_icms?: string;
  cst_pis?: string;
  aliquota_pis?: string;
  cst_cofins?: string;
  aliquota_cofins?: string;
}

// Converte strings vazias do formulário em undefined (não enviar ao backend).
function clean(data: GrupoFormData): Record<string, unknown> {
  return Object.entries(data).reduce((acc, [key, value]) => {
    if (value !== '' && value !== undefined && value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, unknown>);
}

const CreateGrupoTributario: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();

  const formRef = useRef<FormHandles>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { regime, loading: isLoadingRegime } = useRegimeTributario();
  const cstCsosnOptions = useMemo(() => getCstCsosnOptions(regime), [regime]);

  const handleSubmit = useCallback(
    async (data: GrupoFormData) => {
      try {
        setIsLoading(true);
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          descricao: Yup.string().required('Informe a descrição do grupo'),
        });

        await schema.validate(data, { abortEarly: false });

        await api.post(
          `companies/${user.empresa_id}/grupos-tributarios`,
          clean(data),
        );

        addToast({ type: 'success', title: 'Grupo tributário criado' });
        history.push('/grupos-tributarios');
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          formRef.current?.setErrors(getValidationErrors(err));
        } else {
          addToast({
            type: 'error',
            title: 'Erro ao criar grupo tributário',
            // O backend recusa códigos fiscais inexistentes; sem a mensagem
            // dele o usuário não descobre qual campo está errado.
            description: (err as any)?.response?.data?.message,
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [user.empresa_id, addToast, history],
  );

  return (
    <S.Container>
      <header>
        <button
          className="previous_page"
          type="button"
          onClick={() => history.push('/grupos-tributarios')}
        >
          <IoIosArrowBack size={20} />
          Voltar
        </button>
        <div>
          <h1>Adicionar Grupo Tributário</h1>
          <p>Defina as regras de ICMS, PIS e COFINS deste grupo.</p>
        </div>
      </header>

      <Divider />

      <S.CustomForm ref={formRef} onSubmit={handleSubmit}>
        <Input
          name="descricao"
          label="Descrição:"
          placeholder="Ex.: Bebidas - Simples Nacional"
          disabled={isLoading}
        />

        <span className="section-title">ICMS</span>
        <div className="multi">
          <Select
            id="grupoCstCsosn"
            name="cst_csosn"
            label={regime === 'simples_nacional' ? 'CSOSN:' : 'CST/CSOSN:'}
            options={cstCsosnOptions}
            placeholder={
              isLoadingRegime
                ? 'Carregando...'
                : 'Busque pelo código ou descrição'
            }
            isLoading={isLoadingRegime}
            // Enquanto o regime não chega, a lista mostra CSOSN e CST juntos;
            // deixar escolher aí permitiria gravar o código da tabela errada
            // para o regime da empresa.
            isDisabled={isLoadingRegime}
            isClearable
            maxMenuHeight={220}
          />
          <Input
            name="aliquota_icms"
            label="Alíquota ICMS (%):"
            type="number"
            step="0.0001"
            placeholder="Ex.: 18.0000"
          />
          <Input
            name="reducao_base_icms"
            label="Redução base ICMS (%):"
            type="number"
            step="0.0001"
          />
        </div>

        <span className="section-title">PIS</span>
        <div className="multi">
          <Select
            id="grupoCstPis"
            name="cst_pis"
            label="CST PIS:"
            options={CST_PIS_COFINS_OPTIONS}
            placeholder="Busque pelo código ou descrição"
            isClearable
            maxMenuHeight={220}
          />
          <Input
            name="aliquota_pis"
            label="Alíquota PIS (%):"
            type="number"
            step="0.0001"
          />
        </div>

        <span className="section-title">COFINS</span>
        <div className="multi">
          <Select
            id="grupoCstCofins"
            name="cst_cofins"
            label="CST COFINS:"
            options={CST_PIS_COFINS_OPTIONS}
            placeholder="Busque pelo código ou descrição"
            isClearable
            maxMenuHeight={220}
          />
          <Input
            name="aliquota_cofins"
            label="Alíquota COFINS (%):"
            type="number"
            step="0.0001"
          />
        </div>

        <div className="buttons">
          <Button full type="submit" loading={isLoading} disabled={isLoading}>
            Salvar
          </Button>
        </div>
      </S.CustomForm>
    </S.Container>
  );
};

export default CreateGrupoTributario;
